'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Navbar from '@/components/Navbar';
import HeroSlider from '@/components/HeroSlider';
import SearchWidget, { SearchParams } from '@/components/SearchWidget';
import TrustBadgesBar from '@/components/TrustBadgesBar';
import FeaturesSection from '@/components/FeaturesSection';
import DestinationsSection from '@/components/DestinationsSection';
import AboutSection from '@/components/AboutSection';
import TopDestinationsSection from '@/components/TopDestinationsSection';
import TestimonialsSection from '@/components/TestimonialsSection';
import AirlinesSection from '@/components/AirlinesSection';
import Footer from '@/components/Footer';
import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function HomePage() {
  const router = useRouter();
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  const [prefillDestination, setPrefillDestination] = useState<string | null>(null);

  const handleSearch = (params: SearchParams) => {
    const query = new URLSearchParams({
      from: params.origin || '',
      to: params.destination || '',
      departDate: params.departDate || '',
      returnDate: params.returnDate || '',
      tripType: params.tripType || 'roundtrip',
      cabinClass: params.cabinClass || 'Economy',
      adults: String(params.adults || 1),
      children: String(params.children || 0),
      infants: String(params.infants || 0),
    }).toString();
    router.push(`/flights?${query}`);
  };

  const handleDestinationClick = (name: string) => {
    const query = new URLSearchParams({
      from: 'New York (JFK)',
      to: name,
      departDate: '',
      returnDate: '',
      tripType: 'roundtrip',
      cabinClass: 'Economy',
      adults: '1',
      children: '0',
      infants: '0',
    }).toString();
    router.push(`/flights?${query}`);
  };

  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800">
      {/* Navbar */}
      <Navbar />

      {/* Immersive Cinematic Hero Section */}
      <div className="relative min-h-[820px] lg:h-[620px] bg-slate-950 pt-[78px] flex items-center overflow-hidden">
        {/* Absolute dynamic background slides */}
        <div className="absolute inset-0 z-0">
          <HeroSlider />
        </div>

        {/* Dynamic Overlay Content Grid */}
        <div className="relative z-20 max-w-[1170px] mx-auto px-2.5 sm:px-4 w-full py-6 md:py-12 lg:py-0 md:-mt-4 lg:-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-8 lg:gap-12 items-center w-full">
            {/* Left Column: Premium Headline & Value Props */}
            <div className="flex lg:col-span-5 flex-col items-start text-left animate-fadeInUp">
              {/* Glowing Badge */}
              <div
                className="inline-flex items-center gap-1.5 md:gap-2 bg-gradient-to-r from-accent/25 via-blue-500/15 to-brand-slate/25 border border-accent/40 px-3 md:px-4 py-1.8 rounded-full text-[8.5px] md:text-[10px] font-black text-sky-300 uppercase tracking-[0.15em] mb-3 md:mb-5 shadow-glow backdrop-blur-md"
                style={{ height: '30px' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse shadow-[0_0_8px_#0062e3]"></span>
                2026 Unpublished Airfare Access
              </div>

              {/* Bold Title */}
              <h1 className="text-white font-black text-3xl sm:text-4xl md:text-[52px] tracking-tight leading-[1.05] uppercase font-display mb-3 md:mb-5">
                Fly{' '}
                <span className="bg-gradient-to-r from-sky-200 via-blue-400 to-sky-200 bg-clip-text text-transparent">
                  Luxury.
                </span>
                <br />
                <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
                  Pay Half.
                </span>
              </h1>

              {/* Description paragraph */}
              <div className="pl-4 border-l-2 border-accent/40 mb-4 md:mb-6">
                <p className="text-slate-300 text-xs md:text-sm font-medium leading-relaxed max-w-md">
                  Unlock wholesale, phone-only airline discount rates saving up to 50% on commercial
                  tickets. Real-time access to direct relationships with 500+ global air carriers.
                </p>
              </div>

              {/* Checkmarks */}
              <div className="flex flex-wrap gap-2.5 mb-4 md:mb-7">
                <span className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-[10.5px] font-extrabold text-slate-200 shadow-sm backdrop-blur-sm">
                  <svg
                    className="text-accent"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path
                      className="text-slate-950"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      d="m9 11 2 2 4-4"
                    />
                  </svg>
                  PCI Encrypted Checkout
                </span>
                <span className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3.5 py-1.5 text-[10.5px] font-extrabold text-slate-200 shadow-sm backdrop-blur-sm">
                  <svg
                    className="text-accent"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path
                      className="text-slate-950"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      d="m9 11 2 2 4-4"
                    />
                  </svg>
                  24/7 Live Support
                </span>
              </div>

              {/* Quick links */}
              <div className="border-t border-white/10 pt-4 md:pt-5 w-full hidden sm:block">
                <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-[0.12em] block mb-3">
                  Popular Route Quick Search
                </span>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: 'London', code: 'LHR' },
                    { name: 'Paris', code: 'CDG' },
                    { name: 'Tokyo', code: 'NRT' },
                  ].map((dest) => (
                    <button
                      key={dest.code}
                      onClick={() => handleDestinationClick(dest.name)}
                      className="group relative flex items-center gap-2.5 bg-slate-900/40 hover:bg-slate-900/80 border border-white/10 hover:border-accent/40 rounded-xl px-4 py-2 text-white font-bold text-xs uppercase tracking-wider transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0 shadow-sm hover:shadow-premium"
                    >
                      <span className="text-[10px] text-slate-400 group-hover:text-accent transition-colors font-black">
                        {dest.code}
                      </span>
                      <span className="w-[1px] h-3 bg-white/10 group-hover:bg-accent/30 transition-colors pointer-events-none" />
                      <span className="group-hover:text-white transition-colors">{dest.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Search Widget card */}
            <div
              className="lg:col-span-7 w-full animate-fadeInUp"
              style={{ animationDelay: '150ms' }}
            >
              <SearchWidget onSearch={handleSearch} prefillDestination={prefillDestination} />
            </div>
          </div>
        </div>
      </div>

      {/* Premium Content Zone with Dynamic Light Meshes */}
      <div className="relative overflow-hidden bg-slate-50 z-10">
        {/* Soft glowing ambient lighting spheres */}
        <div
          className="absolute top-[10%] left-[-200px] w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-accent/5 via-blue-500/5 to-transparent blur-3xl pointer-events-none z-0 animate-pulse"
          style={{ animationDuration: '8s' }}
        />
        <div
          className="absolute bottom-[20%] right-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-tr from-sky-500/5 via-indigo-500/5 to-transparent blur-3xl pointer-events-none z-0 animate-pulse"
          style={{ animationDuration: '10s' }}
        />

        <TrustBadgesBar />
        <FeaturesSection />
        <DestinationsSection onDestinationClick={handleDestinationClick} />
      </div>

      {/* About */}
      <AboutSection />

      {/* Top Destinations */}
      <TopDestinationsSection onDestinationClick={handleDestinationClick} />

      {/* Testimonials */}
      <TestimonialsSection />

      {/* Airlines */}
      <AirlinesSection />

      {/* Footer */}
      <Footer
        onDestinationClick={(cityName) => {
          // Use a unique key each time to retrigger the effect even if same city
          setPrefillDestination(null);
          setTimeout(() => {
            setPrefillDestination(cityName);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 50);
        }}
      />

      {/* Floating VIP Private Desk Calling Badge */}
      {showPhoneNumber && (
        <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 animate-fadeInUp">
          {/* Style block for gentle float animation */}
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @keyframes gentle-float {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-6px); }
            }
            .vip-desk-float {
              animation: gentle-float 4s infinite ease-in-out;
            }
          `,
            }}
          />
          <a
            href={`tel:${phoneNumber}`}
            className="flex items-center gap-2.5 md:gap-3.5 bg-slate-950/90 backdrop-blur-md border border-white/10 hover:border-accent/40 rounded-2xl p-2.5 md:p-3.5 shadow-premium text-left transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] group vip-desk-float"
            style={{ textDecoration: 'none', minWidth: 'auto', maxWidth: '100%' }}
          >
            {/* Pulsing Phone Ring Badge */}
            <div className="relative flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-[0_0_12px_rgba(0,98,227,0.4)] group-hover:scale-110 transition-transform duration-300">
              <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-25"></span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                className="text-white group-hover:rotate-12 transition-transform duration-300 md:w-[18px] md:h-[18px]"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </div>

            {/* Text descriptions */}
            <div className="flex flex-col hidden sm:flex">
              <span className="text-[8px] md:text-[9.5px] font-extrabold text-slate-400 uppercase tracking-widest leading-none">
                VIP Dedicated Desk
              </span>
              <div className="text-[10px] md:text-xs font-black text-white mt-0.5 tracking-wider">
                {phoneNumber}
              </div>
            </div>
            {/* Mobile version text */}
            <div className="flex flex-col sm:hidden pl-1 pr-2">
              <span className="text-[8px] font-extrabold text-slate-400 uppercase tracking-widest leading-none">
                Call Now
              </span>
              <div className="text-[10px] font-black text-white mt-0.5 tracking-wider">
                {phoneNumber}
              </div>
            </div>
          </a>
        </div>
      )}

      {/* Cookie consent */}
      <CookieConsent />
    </div>
  );
}

function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hasConsented = localStorage.getItem('cookie-consent-accepted');
    if (!hasConsented) {
      setVisible(true);
    }
  }, []);

  const handleAgree = () => {
    localStorage.setItem('cookie-consent-accepted', 'true');
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 sm:right-auto z-50 bg-black/90 sm:bg-black/80 text-white px-4 py-3 sm:py-3 text-[10px] sm:text-sm font-semibold sm:rounded-tr-md sm:max-w-sm flex items-center justify-between sm:block">
      <div className="flex-1 pr-3 sm:pr-0">
        We use cookies. By continuing to browse the site you are agreeing to our use of cookies.{' '}
        <a href="#" className="underline whitespace-nowrap">
          Find out more
        </a>
      </div>
      <button
        onClick={handleAgree}
        className="sm:ml-3 mt-0 sm:mt-2 bg-accent text-white px-3 py-1.5 sm:py-1 rounded text-[10px] sm:text-xs font-bold hover:bg-accent-dark transition-colors whitespace-nowrap"
      >
        Ok, I Agree
      </button>
    </div>
  );
}
